/**
 * Slideshow Section
 * Full-featured Swiper slideshow with pagination, navigation, and autoplay
 */

// Track initialized sections to prevent duplicates
const initializedSlideshows = new WeakSet();

// Queue for sections waiting for Swiper to load
const pendingSlideshowInits = [];

// Store instances for cleanup
const slideshowInstances = new Map();

class SlideshowSlider {
  constructor(section) {
    this.section = section;
    this.swiper = null;
    this.swiperEl = null;

    // Prevent duplicate initialization
    if (initializedSlideshows.has(section)) return;
    initializedSlideshows.add(section);

    // Store instance for potential cleanup
    slideshowInstances.set(section, this);

    this.init();
  }

  /**
   * Get Swiper options from data attributes
   */
  getOptions() {
    // Get options from Liquid data attribute
    let options = {};
    const rawOptions = this.section.dataset.swiperOptions;
    
    if (rawOptions && rawOptions.trim()) {
      try {
        options = JSON.parse(rawOptions);
      } catch (e) {
        console.error('[Slideshow] Failed to parse swiper options:', e, rawOptions);
      }
    }

    // Add navigation selectors (these need JS to build the selectors)
    options.navigation = {
      nextEl: `#${this.section.id} .swiper-button-next`,
      prevEl: `#${this.section.id} .swiper-button-prev`,
    };

    // Handle custom pagination type - render as styled bars
    if (options.pagination && options.pagination.type === 'custom') {
      const paginationColour = this.section.dataset.paginationColour || '#ffffff';
      const paginationColourInactive = this.section.dataset.paginationColourInactive || paginationColour;

      options.pagination.renderCustom = (swiper, current, total) => {
        let bullets = '';
        for (let i = 1; i <= total; i++) {
          const barColour = i === current ? paginationColour : paginationColourInactive;
          bullets += `<span class="block w-10 h-1 rounded-full cursor-pointer transition-colors duration-300" style="background-color: ${barColour};" data-slide-index="${i}"></span>`;
        }
        return `<div class="flex gap-2 justify-center">${bullets}</div>`;
      };

      // Make custom bullets clickable
      this.section.addEventListener('click', (e) => {
        const bullet = e.target.closest('[data-slide-index]');
        if (bullet && this.swiper) {
          const index = parseInt(bullet.dataset.slideIndex, 10) - 1;
          this.swiper.slideTo(index);
        }
      });
    }

    return options;
  }

  /**
   * Initialize Swiper
   */
  init() {
    const Swiper = window.Swiper;

    if (!Swiper) {
      // Queue for later when Swiper loads
      pendingSlideshowInits.push(this);
      return;
    }

    // Find the swiper container by ID
    const sectionId = this.section.id.replace('media-banner-', '');
    this.swiperEl = document.getElementById(`slideshow-${sectionId}`);
    
    if (!this.swiperEl) {
      console.warn('[Slideshow] No slideshow element found');
      return;
    }

    // Check for slides
    const slides = this.swiperEl.querySelectorAll('.swiper-slide');
    if (slides.length < 1) {
      console.warn('[Slideshow] No slides found');
      return;
    }

    // Get options
    const options = this.getOptions();

    // Disable loop if only 1 slide
    if (slides.length === 1) {
      options.loop = false;
      delete options.autoplay;
    }

    // Initialize Swiper
    this.swiper = new Swiper(this.swiperEl, options);

    // Load media only for the active slide; inactive slides stay on data-lazy-* until change
    this.loadActiveSlideMedia({ instant: true });
    this.swiper.on('slideChangeTransitionStart', () => {
      this.loadActiveSlideMedia({ instant: true });
    });

    // Setup visibility observer only if autoplay is enabled
    if (options.autoplay) {
      this.setupVisibilityObserver();
    }
  }

  /**
   * Load lazy images/videos inside the current Swiper slide (skips display:none breakpoints).
   */
  loadActiveSlideMedia({ instant = false } = {}) {
    if (!this.swiper?.slides?.length) return;

    const slide = this.swiper.slides[this.swiper.activeIndex];
    if (!slide) return;

    const media = slide.querySelectorAll(
      '[data-lazy-image]:not(.loaded), [data-lazy-video]:not(.loaded)'
    );

    media.forEach((el) => {
      // Skip the alternate breakpoint image (e.g. mobile asset on desktop)
      if (window.getComputedStyle(el).display === 'none') return;

      if (window.LazyImageLoader?.loadElement) {
        window.LazyImageLoader.loadElement(el, { instant });
      } else {
        // Fallback if lazy-image module has not registered yet
        const src = el.dataset.src;
        const srcset = el.dataset.srcset;
        if (!src) return;
        el.src = src;
        if (srcset) el.srcset = srcset;
        el.classList.add('loaded');
        el.style.opacity = '1';
      }
    });
  }

  /**
   * Pause autoplay when slideshow is not in viewport
   */
  setupVisibilityObserver() {
    if (!this.swiper || !this.swiper.autoplay) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.swiper.autoplay?.start();
          } else {
            this.swiper.autoplay?.stop();
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(this.section);
    this.visibilityObserver = observer;
  }

  /**
   * Destroy the Swiper instance
   */
  destroy() {
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
    }

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  /**
   * Go to a specific slide
   */
  goToSlide(index) {
    if (this.swiper) {
      this.swiper.slideTo(index);
    }
  }

  /**
   * Go to next slide
   */
  next() {
    if (this.swiper) {
      this.swiper.slideNext();
    }
  }

  /**
   * Go to previous slide
   */
  prev() {
    if (this.swiper) {
      this.swiper.slidePrev();
    }
  }
}

/**
 * Initialize all slideshows on the page
 */
function initSlideshows() {
  const sections = document.querySelectorAll('[id^="media-banner-"]');
  sections.forEach((section) => {
    // Only init sections with slideshow-swiper
    if (section.querySelector('.slideshow-swiper')) {
      new SlideshowSlider(section);
    }
  });
}

/**
 * Process any pending initializations once Swiper is available
 */
function processPendingSlideshowInits() {
  if (!window.Swiper || pendingSlideshowInits.length === 0) return;

  while (pendingSlideshowInits.length > 0) {
    const instance = pendingSlideshowInits.shift();
    instance.init();
  }
}

/**
 * Wait for Swiper to be available
 */
function waitForSwiper() {
  if (window.Swiper) {
    processPendingSlideshowInits();
    return;
  }

  // Use Object.defineProperty to detect when Swiper is added to window
  let _swiper = window.Swiper;
  Object.defineProperty(window, 'Swiper', {
    configurable: true,
    get() {
      return _swiper;
    },
    set(value) {
      _swiper = value;
      // Restore normal property behavior
      Object.defineProperty(window, 'Swiper', {
        configurable: true,
        writable: true,
        value: value,
      });
      // Process pending initializations
      processPendingSlideshowInits();
    },
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initSlideshows();
    waitForSwiper();
  });
} else {
  initSlideshows();
  waitForSwiper();
}

// Handle back/forward cache
window.addEventListener('pageshow', (e) => {
  if (e.persisted) {
    initSlideshows();
  }
});

// Export for external use
window.SlideshowSlider = SlideshowSlider;
window.initSlideshows = initSlideshows;
